import { useState, useCallback, useRef } from 'react';

/**
 * Custom hook for caching data with TTL (Time To Live)
 * Reduces redundant API calls
 */
const useDataCache = (ttl = 5 * 60 * 1000) => { // Default 5 minutes
  const cacheRef = useRef(new Map());
  const [cacheStats, setCacheStats] = useState({ hits: 0, misses: 0 });

  const getCacheKey = useCallback((key) => {
    return typeof key === 'object' ? JSON.stringify(key) : String(key);
  }, []);

  const get = useCallback((key) => {
    const cacheKey = getCacheKey(key);
    const cached = cacheRef.current.get(cacheKey);
    
    if (!cached) {
      setCacheStats(prev => ({ ...prev, misses: prev.misses + 1 }));
      return null;
    }

    const now = Date.now();
    if (now - cached.timestamp > ttl) {
      cacheRef.current.delete(cacheKey);
      setCacheStats(prev => ({ ...prev, misses: prev.misses + 1 }));
      return null;
    }

    setCacheStats(prev => ({ ...prev, hits: prev.hits + 1 }));
    return cached.data;
  }, [getCacheKey, ttl]);

  const set = useCallback((key, data) => {
    const cacheKey = getCacheKey(key);
    cacheRef.current.set(cacheKey, {
      data,
      timestamp: Date.now()
    });
  }, [getCacheKey]);

  const clear = useCallback((key) => {
    if (key) {
      const cacheKey = getCacheKey(key);
      cacheRef.current.delete(cacheKey);
    } else {
      cacheRef.current.clear();
      setCacheStats({ hits: 0, misses: 0 });
    }
  }, [getCacheKey]);

  const clearExpired = useCallback(() => {
    const now = Date.now();
    const entries = Array.from(cacheRef.current.entries());
    
    entries.forEach(([key, value]) => {
      if (now - value.timestamp > ttl) {
        cacheRef.current.delete(key);
      }
    });
  }, [ttl]);

  return {
    get,
    set,
    clear,
    clearExpired,
    cacheStats,
    size: cacheRef.current.size
  };
};

export default useDataCache;
